import { createPublicPlayShellReader } from "./public-play-shell";
import { createPlayParticipationReader } from "./play-participation";
import { redirect } from "@tanstack/react-router";
import type { LeaguePortfolioSeatPublic } from "@softmax/observatory-client/types";
import { buildHref } from "@softmax/web-addresses";
import { getActualViewer } from "@/data/viewer";
import {
  resolveGameLeague,
  type GamePageContext,
} from "@/features/game/data/index";
import { onePagerCoworldOf } from "../onepager/coworlds";
import { playLeagueOf } from "../lib/league";
import type { PublicPlayShell } from "../lib/types";
import type { PlayApi } from "./play-api";
export type PlayShellInternal = PublicPlayShell & {
  viewer: {
    playerId: string;
  };
  seat: LeaguePortfolioSeatPublic | null;
  seats: LeaguePortfolioSeatPublic[];
  signedIn: boolean;
  isMember: boolean;
};
export function createPlayShellReader({
  signal,
  context,
  api,
}: {
  signal?: AbortSignal | undefined;
  context: GamePageContext | null | undefined;
  api: PlayApi;
}) {
  const { featuredDivision, publicShell, featuredPublicShell } =
    createPublicPlayShellReader({ signal, context, api });
  const { hasParticipation, canEnter } = createPlayParticipationReader({
    signal,
    api,
  });
  async function loadShell({
    slug,
    includeParticipation,
  }: {
    slug: string;
    includeParticipation: boolean;
  }): Promise<PlayShellInternal | null> {
    const [resolution, viewer] = await Promise.all([
      resolveGameLeague({
        signal,
        publicAddress: slug,
        context: context ?? null,
      }),
      getActualViewer({ signal }),
    ]);

    if (resolution.status === "not-found") return null;

    if (resolution.status !== "ok")
      throw new Error("Play league is unavailable");

    const seats = viewer ? await api.portfolio() : [];

    if (seats === null) throw new Error("League membership is unavailable");

    const seat =
      seats.find((item) => item.league_id === resolution.league.id) ?? null;
    const [featured, isMember] = await Promise.all([
      seat ? null : featuredDivision(resolution.league),
      viewer && includeParticipation
        ? hasParticipation({
            leagueId: resolution.league.id,
            seats,
          })
        : seat !== null,
    ]);
    const divisionId = seat?.division_id ?? featured?.divisionId ?? null;
    const league = playLeagueOf({
      league: resolution.league,
      divisionId,
      divisionName: seat?.division_name ?? featured?.divisionName ?? "League",
    });

    if (includeParticipation && !isMember && !onePagerCoworldOf(league.slug)) {
      throw redirect({
        href: buildHref({ name: "game.start", ...league.route }),
        statusCode: 307,
      });
    }

    return {
      league,
      divisionId,
      viewer: { playerId: seat?.player_id ?? seats[0]?.player_id ?? "" },
      seat,
      seats,
      signedIn: viewer !== null,
      isMember,
    };
  }
  return { publicShell, featuredPublicShell, canEnter, loadShell };
}
